import React, { useMemo, useState } from "react";
import parse, { domToReact, Element, Text } from "html-react-parser";
import { RelatedArticles } from "@/components/modules/Post/ContentComponents/RelatedArticles";
import { InfoBox } from "@/components/modules/Post/ContentComponents/InfoBox";
import { Quote } from "@/components/modules/Post/ContentComponents/Quote";
import { Pool } from "@/components/modules/Post/ContentComponents/Pool";
import { parseMap } from "@/components/parser/Mapper";
import { useRouter } from "next/router";
import { PostType } from "@/core/types/contentTypes";
import { mapAuthor } from "@/connectors/fcWordpress/mappers/mapAuthor";
import { Authors } from "@/components/modules/Post/ContentComponents/Authors";
import { PostImage } from "@/components/modules/Post/ContentComponents/PostImage";
import { LightboxProps } from "@/types/modules/post/components";
import { AdvertisementBlock } from "@/components/layout/AdvertisementBlock";
import { useIsAppOnline } from "@/hooks/useIsAppOnline";

export const useParsedContent = (
  content = "",
  parseBaseHtml = true,
  post?: PostType,
  ignoreAds = false
): [ReturnType<typeof domToReact>, LightboxProps["images"]] => {
  const { locale } = useRouter();
  const isAppOnline = useIsAppOnline();

  /**
   * Combine children of nodeElement into one long string and decode it
   * from base64. This is needed due to super long base64 string that
   * gets split to multiple children in the html-react-parser.
   */
  const decodeBase64NodeElement = (domNodeElement: Element): string => {
    const childrenData: string[] = [];

    domNodeElement.children.forEach((child) => {
      childrenData.push((child as Text).data);
    });

    return atob(childrenData.join(""));
  };

  const allImageSources = useMemo(() => {
    let parsed = parse(content);

    if (parsed instanceof Array) {
      return [
        ...(post?.postDetails?.pageHeaderImage
          ? [
              {
                src: (parse(post?.postDetails?.pageHeaderImage) as JSX.Element)?.props?.src,
                author: post?.postDetails?.pageHeaderAuthor?.name,
                source: post?.postDetails?.pageHeaderSource,
                caption: post?.postDetails?.pageHeaderImageCaption,
              },
            ]
          : []),
        ...parsed
          .filter((el: JSX.Element) => el.type === "figure")
          .map((el: JSX.Element) => {
            if (Array.isArray(el?.props.children)) {
              return {
                src: el?.props.children?.[0]?.props?.src,
                author: el?.props.children?.[0]?.props?.["data-author"],
                source: el?.props.children?.[0]?.props?.["data-source"],
                caption: el?.props.children?.[1]?.props?.children,
              };
            }

            return {
              src: el?.props.children?.props?.src,
            };
          })
          .filter(Boolean),
      ];
    }

    return [];
  }, [content]);

  const parsed = useMemo<ReturnType<typeof domToReact>>(() => {
    let paragraphCounter = 0;
    let displayedFirstAd = false;

    return parse(content, {
      replace: (domNode) => {
        if (!domNode.hasOwnProperty("attribs")) {
          return;
        }
        const domNodeElement = domNode as Element;

        if (domNodeElement.parent) {
          return;
        }

        if (domNodeElement.attribs.class === "block_fc_related_articles") {
          if (!isAppOnline) return <></>;

          const decodedContent = decodeBase64NodeElement(domNodeElement);
          const props = JSON.parse(decodedContent);

          return <RelatedArticles title={props.title} articles={props.articles} isRelatedInArticle />;
        }

        if (domNodeElement.attribs.class === "block_fc_infobox") {
          const decodedContent = decodeBase64NodeElement(domNodeElement);
          const props = JSON.parse(decodedContent);

          return (
            <InfoBox
              title={props.title}
              subtitle={props.subtitle}
              content={props.content}
              contentCollapse={props.content_collapse}
              photo={props.photo}
              photoTitle={props.photo_title}
              photoAuthor={props.photo_author?.display_name}
              source={props.photo_source}
            />
          );
        }

        if (domNodeElement.attribs.class === "block_fc_quote") {
          const decodedContent = decodeBase64NodeElement(domNodeElement);
          const props = JSON.parse(decodedContent);

          return <Quote photo={props.photo} name={props.name} quote={props.quote} alignment={props.alignment} />;
        }

        if (domNodeElement.attribs.class === "block_fc_poll_respondent") {
          const decodedContent = decodeBase64NodeElement(domNodeElement);
          const props = JSON.parse(decodedContent);

          return <Pool photo={props.photo} name={props.name} bio={props.short_bio} jobTitle={props.job_title} />;
        }

        if (domNodeElement.attribs.class === "block_fc_authors") {
          const decodedContent = decodeBase64NodeElement(domNodeElement);
          const props = JSON.parse(decodedContent);

          return <Authors title={props.title} authors={props.authors?.map(mapAuthor)} />;
        }

        if (domNodeElement.attribs.class?.includes("wp-block-image")) {
          const props = (domNodeElement.children[0] as Element).attribs;
          const caption = ((domNodeElement.children[1] as Element)?.children[0] as Text)?.data;
          const displayAd = !ignoreAds && paragraphCounter < 3 && !displayedFirstAd;

          if (displayAd) {
            displayedFirstAd = true;
          }

          return (
            <>
              <PostImage
                source={props?.["data-source"]}
                authorName={props?.["data-author"]}
                caption={caption}
                src={props?.["src"]}
                post={post}
                allImageSources={allImageSources}
              />
              {displayAd && <AdvertisementBlock id={"protag-Hero_in_content_1"} />}
            </>
          );
        }

        //TODO: refactor
        if (["table", "ul", "ol", "tr", "td", "th", "thead"].includes(domNodeElement.name)) {
          return (
            <>
              {domToReact([domNodeElement], {
                replace: (domNode) => {
                  if (!domNode.hasOwnProperty("attribs")) {
                    return;
                  }
                  const domNodeElement = domNode as Element;

                  return parseMap[domNodeElement.name]
                    ? parseMap[domNodeElement.name](domNodeElement, {
                        locale: locale,
                      })
                    : undefined;
                },
              })}
            </>
          );
        }

        if (!parseBaseHtml) return;

        return (
          <>
            {domToReact([domNodeElement], {
              replace: (domNode) => {
                if (!domNode.hasOwnProperty("attribs")) {
                  return;
                }
                const domNodeElement = domNode as Element;

                if (parseMap[domNodeElement.name] && domNodeElement.name === "p") {
                  paragraphCounter++;
                  return parseMap[domNodeElement.name](domNodeElement, {
                    locale,
                    paragraphCounter,
                    displayedFirstAd,
                    ignoreAds: ignoreAds,
                  });
                }

                return parseMap[domNodeElement.name]
                  ? parseMap[domNodeElement.name](domNodeElement, {
                      locale: locale,
                    })
                  : undefined;
              },
            })}
          </>
        );
      },
    });
  }, [content]);

  return [parsed, allImageSources];
};
